import { z } from "zod/v4"

// ---------------------------------------------------------------------------
// Dashboard コレクション Firestore ドキュメント型定義
//
// コレクション: dashboard/{auto-generated-id}
// フラット構造で、span / kind / date フィールドによりフィルタ・クエリする。
// ---------------------------------------------------------------------------

// --- Enum ---

/** 集計期間の粒度 */
export const DashboardSpan = z.enum(["daily", "monthly"])
export type DashboardSpan = z.infer<typeof DashboardSpan>

/** データの種類（集計対象のドメイン） */
export const DashboardKind = z.enum([
  // Phase 1
  "access",
  "new-users",
  "retention",
  "engagement",
  "subscription",
  "alerts",
  "ga4-access",
])
export type DashboardKind = z.infer<typeof DashboardKind>

// --- 共通フィールド ---

/** 全 dashboard ドキュメント共通のメタフィールド */
export const DashboardDocumentBase = z.object({
  span: DashboardSpan,
  kind: DashboardKind,
  /** "YYYY-MM-DD" (daily) or "YYYY-MM" (monthly) */
  date: z.string(),
  /** 集計実行日時 (ISO8601) */
  generated_at: z.string(),
  /** スキーマバージョン */
  version: z.string(),
})

export type DashboardDocumentBase = z.infer<typeof DashboardDocumentBase>

// --- 共通の値オブジェクト ---

/** DAU/WAU/MAU の値セット */
export const DauMauValues = z.object({
  dau: z.number().int().describe("日間アクティブユーザー数"),
  wau: z.number().int().describe("週間アクティブユーザー数"),
  mau: z.number().int().describe("月間アクティブユーザー数"),
})

export type DauMauValues = z.infer<typeof DauMauValues>

// --- kind: "access" ---

/**
 * kind="access", span="daily" のデータ
 *
 * DAU/WAU/MAU の定義について:
 *   本 kind の dau/wau/mau は「通話・配信・視聴のいずれかを行ったユーザー」をカウントする。
 *   具体的には以下 3 ソースの UNION DISTINCT:
 *     - 通話: spaces_space_users (status=online, CREATE or UPDATE)
 *     - 配信: spaces_lives (CREATE)
 *     - 視聴: spaces_lives_live_viewers (CREATE)
 *   "アプリ起動" ベースの DAU (一般的なアクティブユーザー指標) は backend からは正確に
 *   観測できないため、別途 GA4 の user_engagement イベント (web stream 除外) を
 *   ベースに別 kind で扱う予定 (本 kind の責務外)。
 */
export const DashboardAccessDaily = z.object({
  north_star: DauMauValues,
  north_star_prev_month: DauMauValues,
  dau: z.number().int().describe("通話・配信・視聴のいずれかを行った日次ユニークユーザー数"),
  wau: z.number().int().describe("通話・配信・視聴のいずれかを行った週次ユニークユーザー数"),
  mau: z.number().int().describe("通話・配信・視聴のいずれかを行った月次ユニークユーザー数"),
  dau_mau_ratio: z.number().describe("DAU/MAU 比率"),
  new_users: z.number().int().describe("新規ユーザー数"),
  churn_count: z.number().int().describe("チャーンユーザー数"),
  dormant_users: z.number().int().describe("休眠ユーザー数 (通話・配信・視聴のいずれも 30 日間行っていないユーザー数)"),
  churn_rate: z.number().describe("チャーン率"),
  total_users: z.number().int().describe("総ユーザー数"),
  streamers: DauMauValues.describe("配信者数"),
  viewers: DauMauValues.describe("視聴者数"),
})

export type DashboardAccessDaily = z.infer<typeof DashboardAccessDaily>

/** kind="access", span="monthly" のデータ */
export const DashboardAccessMonthly = z.object({
  avg_dau: z.number().int(),
  max_dau: z.number().int(),
  avg_new_users: z.number().int(),
  total_new_users: z.number().int(),
  total_churn_count: z.number().int(),
  avg_north_star_dau: z.number().int(),
  max_north_star_dau: z.number().int(),
  mau: z.number().int(),
  daily_data: z.array(z.object({
    date: z.string(),
    dau: z.number().int(),
    north_star_dau: z.number().int(),
    new_users: z.number().int(),
    churn_count: z.number().int(),
  })),
})

export type DashboardAccessMonthly = z.infer<typeof DashboardAccessMonthly>

// --- kind: "new-users" ---

/** Aha Moment（初回体験）データ */
export const AhaMomentData = z.object({
  new_users: z.number().int().describe("新規ユーザー数"),
  joined_space: z.number().int(),
  joined_space_rate: z.number(),
  first_follow: z.number().int(),
  first_follow_rate: z.number(),
  first_mutual_follow: z.number().int(),
  first_mutual_follow_rate: z.number(),
  first_live_view: z.number().int(),
  first_live_view_rate: z.number(),
  first_live_stream: z.number().int(),
  first_live_stream_rate: z.number(),
})

export type AhaMomentData = z.infer<typeof AhaMomentData>

/** kind="new-users", span="daily" のデータ */
export const DashboardNewUsersDaily = z.object({
  count: z.number().int().describe("新規ユーザー数"),
  invite_funnel: z.object({
    invite_created: z.number().int().describe("招待作成数"),
    invite_used: z.number().int().describe("招待使用数"),
  }),
  aha_moment: AhaMomentData,
})

export type DashboardNewUsersDaily = z.infer<typeof DashboardNewUsersDaily>

// --- kind: "retention" ---

/** コホート継続率の 1 エントリ */
export const CohortRetentionEntry = z.object({
  retained: z.number().int().describe("継続ユーザー数"),
  rate: z.number().describe("継続率"),
})

export type CohortRetentionEntry = z.infer<typeof CohortRetentionEntry>

/**
 * 1 コホートの継続率データ
 *
 * 全ての D 値は optional。snapshot 日 (doc の date) とコホート登録日の差分が K 日未満の場合、
 * 当該 dK はまだ経過していない（計測対象外）ため doc に含めない。
 * UI ではその D 値を「—」と表示する。
 */
export const CohortRetention = z.object({
  cohort_size: z.number().int().describe("コホートサイズ"),
  d1: CohortRetentionEntry.optional(),
  d2: CohortRetentionEntry.optional(),
  d3: CohortRetentionEntry.optional(),
  d4: CohortRetentionEntry.optional(),
  d5: CohortRetentionEntry.optional(),
  d6: CohortRetentionEntry.optional(),
  d7: CohortRetentionEntry.optional(),
  d8: CohortRetentionEntry.optional(),
  d9: CohortRetentionEntry.optional(),
  d10: CohortRetentionEntry.optional(),
  d11: CohortRetentionEntry.optional(),
  d12: CohortRetentionEntry.optional(),
  d13: CohortRetentionEntry.optional(),
  d14: CohortRetentionEntry.optional(),
  d21: CohortRetentionEntry.optional(),
  d30: CohortRetentionEntry.optional(),
  d60: CohortRetentionEntry.optional(),
  d90: CohortRetentionEntry.optional(),
})

export type CohortRetention = z.infer<typeof CohortRetention>

/** kind="retention", span="daily" のデータ */
export const DashboardRetentionDaily = z.object({
  /** GA4 アプリ起動ベース全体継続率（キー = コホート日付 YYYY-MM-DD） */
  ga4_general: z.record(z.string(), CohortRetention),
  /** 行動ベース全体継続率 */
  general_action: z.record(z.string(), CohortRetention),
  /** スペース参加ベース継続率 */
  space_based: z.record(z.string(), CohortRetention),
  /** LIVE 視聴ベース継続率 */
  live_view_based: z.record(z.string(), CohortRetention),
})

export type DashboardRetentionDaily = z.infer<typeof DashboardRetentionDaily>

// --- kind: "engagement" ---

/** kind="engagement", span="daily" のデータ */
export const DashboardEngagementDaily = z.object({
  spaces_created: z.number().int().describe("スペース作成数（当日 CREATE された spaces 数）"),
  space_join_users: DauMauValues.describe(
    "スペースメンバー登録ユーザー数（当日 space-users コレクションに新規 CREATE されたユニーク user_id）",
  ),
  space_active_users: DauMauValues.describe(
    "スペース通話接続ユーザー数（当日 space-users の status=online になったユニーク user_id）",
  ),
  live: z.object({
    count: z.number().int().describe("LIVE 配信数（当日 CREATE された lives 数）"),
    unique_streamers: DauMauValues.describe("ユニーク配信者数"),
    total_viewers: z.number().int().describe("延べ視聴者数（当日 CREATE された live-viewers 件数）"),
    unique_viewers: DauMauValues.describe("ユニーク視聴者数"),
    avg_peak_viewers: z.number().describe("1 配信あたりの平均ピーク視聴者数"),
    total_duration_minutes: z.number().describe("配信時間合計（分、分子）"),
    ended_live_count: z.number().int().describe("終了済み LIVE 数（分母）"),
  }),
  social: z.object({
    new_follows: z.number().int().describe("新規フォロー数"),
    comments: z.number().int().describe("LIVE コメント数"),
    space_messages: z.number().int().describe("スペースメッセージ数（LIVE 外のスペース内メッセージ）"),
    reactions: z.number().int().describe("LIVE リアクション数"),
    stories: z.number().int().describe("ストーリー投稿数"),
  }),
})

export type DashboardEngagementDaily = z.infer<typeof DashboardEngagementDaily>

// --- kind: "subscription" ---

/** サブスクリプションイベントのプラットフォーム別内訳 */
export const SubscriptionEventCounts = z.object({
  total: z.number().int().describe("合計"),
  iap: z.number().int().describe("App Store (IAP)"),
  iab: z.number().int().describe("Google Play (IAB)"),
})

export type SubscriptionEventCounts = z.infer<typeof SubscriptionEventCounts>

/** kind="subscription", span="daily" のデータ */
export const DashboardSubscriptionDaily = z.object({
  active: z.object({
    total: z.number().int().describe("アクティブサブスク合計"),
    premium: z.number().int().describe("プレミアム"),
    premium_plus: z.number().int().describe("プレミアムプラス"),
  }),
  subscription_rate: z.number().describe("サブスクリプション率"),
  events: z.object({
    subscribe: SubscriptionEventCounts,
    cancel: SubscriptionEventCounts,
    expire: SubscriptionEventCounts,
    renew: SubscriptionEventCounts,
    upgrade: SubscriptionEventCounts,
    downgrade: SubscriptionEventCounts,
  }),
})

export type DashboardSubscriptionDaily = z.infer<typeof DashboardSubscriptionDaily>

// --- kind: "alerts" ---

/** kind="alerts", span="daily" のデータ */
export const DashboardAlertsDaily = z.object({
  churn_count: z.number().int().describe("チャーンユーザー数"),
  violation_reports: z.object({
    total: z.number().int().describe("通報合計"),
    /** 理由別の通報数（キー = 理由名） */
    by_reason: z.record(z.string(), z.number().int()),
    /** 対象別の通報数（キー = 対象種別） */
    by_target: z.record(z.string(), z.number().int()),
  }),
})

export type DashboardAlertsDaily = z.infer<typeof DashboardAlertsDaily>

// --- メタ情報ドキュメント ---

// --- kind: "ga4-access" ---

/**
 * kind="ga4-access", span="daily" のデータ
 *
 * GA4 の user_engagement イベント（web stream 除外）から user_pseudo_id で
 * ユニークカウントした「アプリ起動 DAU」を格納する。
 * アカウント未作成ユーザーも含むデバイスベースの値。
 *
 * GA4 プロパティ ID は non-secret config の ga4PropertyId で環境ごとに設定する。
 */
export const DashboardGa4AccessDaily = z.object({
  dau: z.number().int().describe("GA4 起動 DAU（当日の user_engagement イベントのユニークデバイス数）"),
})

export type DashboardGa4AccessDaily = z.infer<typeof DashboardGa4AccessDaily>

// --- メタ情報 ---

/**
 * dashboard-meta/latest ドキュメントの型
 *
 * コレクション: dashboard-meta/{documentId}
 * バッチ実行のメタ情報を格納する。dashboard コレクションとは別コレクション。
 */
export const DashboardMeta = z.object({
  last_updated: z.string().describe("最終更新日時 (ISO8601)"),
  latest_date: z.string().describe("最新データ日付"),
  earliest_date: z.string().describe("最古データ日付"),
  version: z.string().describe("スキーマバージョン"),
  status: z.enum(["success", "partial_failure", "failure"]).describe("最終バッチ実行ステータス"),
})

export type DashboardMeta = z.infer<typeof DashboardMeta>
